import { Column } from "@tanstack/react-table";
import { RxEyeNone } from "react-icons/rx";
import { ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils/ui";

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  icon?: React.ReactNode;
  showHideButton?: boolean;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  icon,
  className,
  showHideButton = false,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const handleSortCycle = () => {
    const sorted = column.getIsSorted();
    if (sorted === "asc") {
      column.toggleSorting(true);
      return;
    }
    if (sorted === "desc") {
      column.clearSorting();
      return;
    }
    column.toggleSorting(false);
  };

  const sortIcon =
    column.getIsSorted() === "desc" ? (
      <ArrowDownWideNarrow className="h-4 w-4" />
    ) : (
      <ArrowUpNarrowWide className="h-4 w-4" />
    );

  const sortIconClassName =
    column.getIsSorted() === false
      ? "ml-2 opacity-0 group-hover:opacity-100"
      : "ml-2 text-primary";

  if (!column.getCanSort()) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span>{title}</span>
      </div>
    );
  }

  if (!showHideButton) {
    return (
      <div className={cn("flex items-center space-x-2", className)}>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 group"
          onClick={handleSortCycle}
        >
          {icon && <span className="text-muted-foreground">{icon}</span>}
          <span>{title}</span>
          <span className={sortIconClassName}>{sortIcon}</span>
        </Button>
      </div>
    );
  }
  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 data-[state=open]:bg-accent group"
            onClick={handleSortCycle}
          >
            {icon && <span className="text-muted-foreground">{icon}</span>}
            <span>{title}</span>
            <span className={sortIconClassName}>{sortIcon}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
            <ArrowUpNarrowWide className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
            <ArrowDownWideNarrow className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Desc
          </DropdownMenuItem>
          {showHideButton && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
                <RxEyeNone className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                Hide
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
